import React, { useState } from 'react';
import { useSeedanceAuthStore } from '../../../../store/seedanceAuthStore';
import { useSeedanceUiStore } from '../../../../store/seedanceUiStore';
import './seedanceModals.css';

interface SeedanceModalsProps {
  envCheckMsg: React.ReactNode;
  onWelcomeStart: () => void;
  onWelcomeSkip: () => void;
  onSaveConfig: () => void;
}

export const SeedanceModals: React.FC<SeedanceModalsProps> = ({
  envCheckMsg,
  onWelcomeStart,
  onWelcomeSkip,
  onSaveConfig,
}) => {
  const {
    welcomeVisible,
    welcomeKey,
    setWelcomeKey,
    configVisible,
    setConfigVisible,
  } = useSeedanceUiStore();

  const {
    systemToken,
    setSystemToken,
    customGroupId,
    setCustomGroupId,
    configUserId,
    setConfigUserId,
  } = useSeedanceAuthStore();

  const [showSystemToken, setShowSystemToken] = useState(false);

  return (
    <>
      {/* Welcome */}
      <div className={`welcome-mask${welcomeVisible ? '' : ' hidden'}`}>
        <div className="welcome-box">
          <button className="welcome-close" onClick={onWelcomeSkip} aria-label="关闭弹窗">
            ×
          </button>
          <div className="welcome-logo">
            <div className="welcome-logo-ico">🎬</div>
            <div>
              <h2>首页科技</h2>
              <p>AI 视频生成工具 · 本地版 · 由火山引擎 Seedance 驱动</p>
            </div>
          </div>

          <div className="welcome-step">
            <div className="welcome-step-label">
              <span>1</span> 填写火山引擎 API Key
            </div>
            <input
              className="welcome-input"
              type="password"
              placeholder="在这里粘贴您的 API Key…"
              value={welcomeKey}
              onChange={(e) => setWelcomeKey(e.target.value)}
            />
            <div className="welcome-hint">
              <a href="https://ai.k23.cn/" target="_blank" rel="noreferrer">
                注册并获取密钥
              </a>
            </div>
          </div>

          <div className="welcome-step">
            <div className="welcome-step-label">
              <span>2</span> 运行环境检测
            </div>
            <div
              style={{
                background: 'var(--bg-900)',
                borderRadius: 10,
                padding: 14,
                marginBottom: 4,
              }}
            >
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{envCheckMsg}</div>
            </div>
          </div>

          <button className="w-start-btn" onClick={onWelcomeStart}>
            🚀 开始使用
          </button>
          <div className="w-skip" onClick={onWelcomeSkip}>
            暂时跳过，直接进入（部分功能可能受限）
          </div>
        </div>
      </div>

      {/* Task config modal */}
      <div className={`config-mask${configVisible ? '' : ' hidden'}`}>
        <div className="config-box">
          <button className="config-close" onClick={() => setConfigVisible(false)} aria-label="关闭配置弹窗">
            ×
          </button>
          <h3>⚙️ 任务/素材接口配置</h3>
          <div className="config-field">
            <label>设置安全令牌</label>
            <div className="config-input-wrap">
              <input
                className="config-input"
                type={showSystemToken ? 'text' : 'password'}
                value={systemToken}
                onChange={(e) => setSystemToken(e.target.value)}
                placeholder="请输入安全令牌"
              />
              <button
                type="button"
                className="config-input-toggle"
                onClick={() => setShowSystemToken((v) => !v)}
                aria-label={showSystemToken ? '隐藏令牌' : '显示令牌'}
                title={showSystemToken ? '隐藏令牌' : '显示令牌'}
              >
                {showSystemToken ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M3 3l18 18M10.58 10.58A2 2 0 0 0 12 15a2 2 0 0 0 1.42-.58M9.88 5.09A10.94 10.94 0 0 1 12 5c5 0 9.27 3.11 10 7a10.94 10.94 0 0 1-1.88 3.09M6.1 6.1A10.94 10.94 0 0 0 2 12c.73 3.89 5 7 10 7a10.94 10.94 0 0 0 4.9-1.1"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="config-field">
            <label>设置分组ID</label>
            <div className="config-input-wrap">
              <input
                className="config-input"
                type="text"
                value={customGroupId}
                onChange={(e) => setCustomGroupId(e.target.value)}
                placeholder="请输入分组ID（选填）"
              />
            </div>
            <div className="config-hint">
              设置ID将作为项目状态中的分组ID。如果不设置，则使用接口请求到的分组ID。
            </div>
          </div>
          <div className="config-field">
            <label>用户ID</label>
            <input
              className="config-input"
              type="text"
              value={configUserId}
              onChange={(e) => setConfigUserId(e.target.value)}
              placeholder="请输入用户ID"
            />
            <div className="config-hint">用户ID为首页科技「个人设置」页面中的 ID。</div>
          </div>
          <div className="config-actions">
            <button className="btn" onClick={() => setConfigVisible(false)}>
              取消
            </button>
            <button className="btn primary" onClick={onSaveConfig}>
              保存
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
